import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Reservation } from 'models/reservation';
import { ReduxState } from 'ducks';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { lookupReservationsByAccessToken } from 'ducks/client/reservation';
import { useGoogleMapsApi } from 'components/GuidancePageList/util';
import { CurrentPositionContext } from 'contexts/CurrentPositionContext';
import { ETicketRedeemTargetContext, RedeemTarget } from 'contexts/ETicketRedeemTargetContext';
import { getCheckinErrorReason } from 'lib/util/getCheckinErrorReason';
import { useProductDetails } from 'hooks/useProductDetails';
import { ETicketCheckinModal } from 'components/ETicketCheckinModal/ETicketCheckinModal';
import { ShowRedeemedETicketCardModal } from 'components/ETicketPage/ShowRedeemedETicketCardModal';
import { useLiff } from 'hooks/useLiff';
import { useLoading } from 'hooks/useLoading';
import { toProductSummaryShape } from 'lib/util/productSummaryShape';
import { ProductSummary } from 'models/product';

import styles from './ETickets.module.css';
import { ETicketCard } from './ETicketCard';
import { getStatus, isCheckinedStub } from './utils';
import { triggerSalesOffer } from 'ducks/client/salesOffers';

interface Props {
  host: string;
}

export const ETickets = ({ host }: Props) => {
  const [showTickets, setShowTickets] = React.useState<boolean>(false);

  React.useEffect(() => {
    setShowTickets(true);
  }, []);

  if (!showTickets) {
    return null;
  }

  return <ETicketsContent host={host} />;
};

export const ETicketsContent = ({ host }: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const { accessToken } = useLiff();
  const { setLoading } = useLoading();

  const { t, i18n } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);

  const foundReservations = useSelector((state: ReduxState) => state.reservation.foundReservations);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const eTicketQrCode = useSelector(
    (state: ReduxState) => state.server.eTicketQrCode.eTicketQrCode
  );

  const [currentPosition, setCurrentPosition] = React.useState<google.maps.LatLng | null>(null);
  const [redeemTarget, setRedeemTarget] = React.useState<RedeemTarget | null>(null);
  const [ignoreQrCodeLink, setIgnoreQrCodeLink] = React.useState<boolean>(false);
  const [redeemedReservationId, setRedeemedReservationId] = React.useState<string | null>(null);

  const { productDetails } = useProductDetails(
    Array.from(new Set(foundReservations.map((r) => r.product_id))),
    apiKey,
    i18n.language
  );

  const productSummariesFromDetails = React.useMemo(() => {
    if (!productDetails) {
      return [];
    }
    return productDetails.map((productDetail) => {
      return toProductSummaryShape(productDetail as ProductSummary);
    });
  }, [productDetails]);

  const { isLoaded } = useGoogleMapsApi();
  React.useEffect(() => {
    if (isLoaded) {
      const id = navigator.geolocation.watchPosition((pos) => {
        const coords = pos.coords;

        if (coords.latitude && coords.longitude) {
          const latLng = { lat: coords.latitude, lng: coords.longitude };

          const googleLatLng = new google.maps.LatLng(latLng);
          setCurrentPosition(googleLatLng);
          dispatch(
            triggerSalesOffer({
              triggerType: 'ONLINE_GUIDE_PROXIMITY_TO_KEY_LOCATION',
              currentPosition: googleLatLng,
            })
          );
        }
      });

      return () => navigator.geolocation.clearWatch(id);
    }
  }, [isLoaded]);

  const reservations = React.useMemo(() => {
    if (foundReservations.length != 0) {
      return [
        ...foundReservations.filter((reservation) => {
          return reservation.status === 'CONFIRMED';
        }),
      ];
    }
  }, [foundReservations]);

  const sortedReservations = (reservations ?? []).sort((a, b) => {
    if (
      moment
        .tz(a.start_date_time_utc, a.start_timezone)
        .isBefore(moment.tz(b.start_date_time_utc, b.start_timezone))
    ) {
      return -1;
    }
    return 1;
  });

  React.useEffect(() => {
    const fn = async () => {
      if (accessToken) {
        try {
          setLoading?.(true);
          await dispatch(
            lookupReservationsByAccessToken(
              apiKey,
              i18n.language,
              accessToken || '',
              '',
              null,
              null,
              ['CONFIRMED'],
              process.env.NEXT_PUBLIC_LOOKUP_RESERVATIONS_START_DATE
                ? moment()
                    .add(`-${process.env.NEXT_PUBLIC_LOOKUP_RESERVATIONS_START_DATE}`, 'days')
                    .format('YYYY-MM-DD')
                : undefined,
              undefined
            )
          );
        } catch (err) {
          console.log(err);
        } finally {
          setLoading?.(false);
        }
      }
    };
    fn();
  }, [accessToken, apiKey, i18n.language]);

  React.useEffect(() => {
    if (accessToken && router?.query?.ticketPlateId && !router.query?.ticketRecorded) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            ticketRecorded: true,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  }, [accessToken, router]);

  const [
    startedReservations,
    notStartedReservations,
    expiredReservations,
    defaultCheckinReservations,
  ] = React.useMemo(() => {
    const startedReservations: Reservation[] = [];
    const notStartedReservations: Reservation[] = [];
    const expiredReservations: Reservation[] = [];
    const defaultCheckinReservations: Reservation[] = [];

    sortedReservations.forEach((r) => {
      const product = productSummariesFromDetails.find((product) => {
        return product.id === r.product_id;
      });
      if (!product) {
        return false;
      }

      const status = getStatus(r, product);

      if (!ignoreQrCodeLink) {
        const qrCheckinLink = (eTicketQrCode?.qr_code_links ?? []).find((link) => {
          return link.product_id === r.product_id;
        });
        if (qrCheckinLink) {
          const reason = getCheckinErrorReason(r, t);
          if (!reason) {
            if (
              (qrCheckinLink.stub_key && !isCheckinedStub(r, qrCheckinLink.stub_key)) ||
              (!qrCheckinLink.stub_key && status === 'not_started')
            ) {
              if (defaultCheckinReservations.length === 0) {
                defaultCheckinReservations.push(r);
                setRedeemTarget({
                  reservation: r,
                  stubKey: qrCheckinLink.stub_key,
                  stubOptionKey: qrCheckinLink.stub_option_key,
                });
                return;
              }
            }
          }
        }
      }

      if (status === 'started') {
        startedReservations.push(r);
      } else if (status === 'not_started') {
        notStartedReservations.push(r);
      } else if (status === 'expired') {
        expiredReservations.push(r);
      }
    });

    return [
      startedReservations,
      notStartedReservations,
      expiredReservations,
      defaultCheckinReservations,
    ];
  }, [sortedReservations, productSummariesFromDetails, eTicketQrCode]);

  const startedTicketExists = startedReservations.length > 0;

  return (
    <CurrentPositionContext.Provider value={{ currentPosition }}>
      <ETicketRedeemTargetContext.Provider value={{ redeemTarget, setRedeemTarget }}>
        <div className={clsx(styles['wrap'])}>
          <header className={clsx(styles['header'])}>
            <Link href={'/'} className={clsx(styles['header__logo'])}>
              <img src={settings?.logo_url} />
            </Link>
          </header>
          <main className={clsx(styles['main'])}>
            <article className={clsx(styles['main__list'])}>
              {defaultCheckinReservations.map((reservation) => {
                return (
                  <ETicketCard
                    reservation={reservation}
                    initialOpen={true}
                    key={reservation.id}
                    product={productDetails?.find((product) => {
                      return product.id === reservation.product_id;
                    })}
                    host={host}
                  />
                );
              })}
              {startedReservations.map((reservation) => {
                return (
                  <ETicketCard
                    reservation={reservation}
                    initialOpen={true}
                    key={reservation.id}
                    product={productDetails?.find((product) => {
                      return product.id === reservation.product_id;
                    })}
                    host={host}
                  />
                );
              })}
              {notStartedReservations.map((reservation, idx) => {
                return (
                  <ETicketCard
                    reservation={reservation}
                    initialOpen={!startedTicketExists && idx === 0}
                    key={reservation.id}
                    product={productDetails?.find((product) => {
                      return product.id === reservation.product_id;
                    })}
                    host={host}
                  />
                );
              })}
              {expiredReservations.map((reservation) => {
                return (
                  <ETicketCard
                    reservation={reservation}
                    initialOpen={false}
                    key={reservation.id}
                    product={productDetails?.find((product) => {
                      return product.id === reservation.product_id;
                    })}
                    host={host}
                  />
                );
              })}
              <section className={clsx(styles['main__list__item'])}>
                <p className={clsx(styles['last__terms'])}>
                  {t('These are the tickets already purchased')}
                </p>
              </section>
            </article>
          </main>
        </div>
        {redeemTarget && (
          <ETicketCheckinModal
            reservation={redeemTarget.reservation}
            stubKey={redeemTarget.stubKey}
            initialStubOptionKey={redeemTarget.stubOptionKey}
            open={Boolean(redeemTarget)}
            onSuccessfulCheckin={() => {
              const product = (productDetails || []).find((product) => {
                return product.id === redeemTarget?.reservation.product_id;
              });
              if (product?.qr_checkin_settings?.expiration_date?.instant_expiration) {
                setRedeemedReservationId(redeemTarget.reservation.id ?? '');
              }
            }}
            onClose={() => {
              setRedeemTarget(null);
              setIgnoreQrCodeLink(true);
            }}
            guestTypeCounts={redeemTarget.guestTypeCounts}
            product={productDetails?.find((product) => {
              return product.id === redeemTarget.reservation.product_id;
            })}
          />
        )}
        {redeemedReservationId && (
          <ShowRedeemedETicketCardModal onClose={() => setRedeemedReservationId(null)}>
            <ETicketCard
              reservation={sortedReservations.find((r) => r.id === redeemedReservationId)}
              product={productDetails?.find((product) => {
                return (
                  product.id ===
                  sortedReservations.find((r) => r.id === redeemedReservationId)?.product_id
                );
              })}
              initialOpen={true}
              host={host}
            ></ETicketCard>
          </ShowRedeemedETicketCardModal>
        )}
      </ETicketRedeemTargetContext.Provider>
    </CurrentPositionContext.Provider>
  );
};
